import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';
import { getPublicChannelItems, listPublicAnnouncements } from '../../services/content';
import { ContentItem } from '../../types/content';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';

interface CarouselSlide {
  title: string;
  desc: string;
  points: string[];
  image: string;
  link?: string;
}

interface ContactQrItem {
  id: number;
  title: string;
  image: string;
  summary: string;
}

interface FriendlyLinkItem {
  id: number;
  title: string;
  url: string;
}

const pickFirstImageFromBody = (html: string): string => {
  const raw = String(html || '').trim();
  if (!raw) return '';
  const matched = raw.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
  return matched?.[1] ? String(matched[1]).trim() : '';
};

const fallbackCarouselSlides: CarouselSlide[] = [
  {
    title: '美国仓储与订单履约服务',
    desc: '已开通仓库 1 个',
    points: ['位置：美东 Nashua NH 03060', '仓库面积：7000 尺', '团队规模：员工 2 人'],
    image:
      'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1400&q=80',
    link: '',
  },
  {
    title: '已开通服务',
    desc: '海外仓 / 仓配托管',
    points: [
      '一件代发/订单履约',
      '备货中转',
      '移仓换标',
      '退货处理',
      '检测翻新等业务',
    ],
    image:
      'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=80',
    link: '',
  },
  {
    title: '未来仓库网络（规划中）',
    desc: '全球多区域仓网持续建设',
    points: ['美东', '美西', '中国大陆', '香港', '...'],
    image:
      'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=1400&q=80',
    link: '',
  },
  {
    title: '未来服务板块（规划中）',
    desc: '全场景供应链服务持续扩展',
    points: [
      '海外仓 / 仓配托管',
      '快递物流',
      '包裹转运集运',
      '商品采购销售代理',
    ],
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80',
    link: '',
  },
];

const businessBlocks = [
  {
    key: 'warehouse',
    title: '海外仓 / 仓配托管',
    overview:
      '服务对象为本土或跨境卖家，提供线上多平台或线下传统分销渠道的订单履约、备货中转、移仓换标、退货处理、检测翻新等服务内容。',
    status: '已开通',
    image: '/warehouse-3pl.png',
  },
  {
    key: 'transit',
    title: '包裹转运/集运',
    overview:
      '服务对象为本土或跨境商品转运/集运客户，提供代收包裹、检测验货、拍照或视频、临时仓储、合箱分箱、转运出库、退货等服务内容。',
    status: '规划中',
    image: '/transit-conveyor.png',
  },
  {
    key: 'logistics',
    title: '快递物流',
    overview:
      '服务对象为本土的直发快递物流客户，提供跨境快递小包裹、行李运输等服务，运输方式包括海运和空运。',
    status: '规划中',
    image:
      'https://images.unsplash.com/photo-1565891741441-64926e441838?auto=format&fit=crop&w=1200&q=80',
  },
  {
    key: 'agency',
    title: '采购 / 销售代理',
    overview:
      '服务对象为本土或跨境买家/卖家，提供代理采购及多渠道产品分销服务。',
    status: '规划中',
    image:
      'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=1200&q=80',
  },
];

const serviceAdvantages = [
  '自营仓库一手价格',
  '收费透明无隐藏费用',
  '管理层级少 沟通及时高效',
  '服务方式和收费标准可灵活定制',
  '自研管理系统贴近业务现实 操作简便',
  '全系统记录 业务数据准确及时',
];

const isExternalUrl = (url: string) => /^(https?:)?\/\//i.test(url) || /^(mailto|tel):/i.test(url);

const MarketingHomePage: React.FC = () => {
  useDocumentMeta({
    description: '安飞供应链(AnFei Supply Chain)专注中美跨境供应链服务，提供海外仓/仓配托管、包裹转运集运、快递物流与采购销售代理，助力跨境电商卖家降本增效。',
  });

  const [slideIndex, setSlideIndex] = useState(0);
  const [carouselSlides, setCarouselSlides] = useState<CarouselSlide[]>(fallbackCarouselSlides);
  const [announcementRows, setAnnouncementRows] = useState<ContentItem[]>([]);
  const [announcementTotal, setAnnouncementTotal] = useState(0);
  const [contactQrRows, setContactQrRows] = useState<ContactQrItem[]>([]);
  const [friendlyLinks, setFriendlyLinks] = useState<FriendlyLinkItem[]>([]);
  const [previewQr, setPreviewQr] = useState<ContactQrItem | null>(null);
  const [activeBizKey, setActiveBizKey] = useState('warehouse');
  const [hoverBizKey, setHoverBizKey] = useState<string | null>(null);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const normalizedSlides = carouselSlides.length ? carouselSlides : fallbackCarouselSlides;
  const totalSlides = normalizedSlides.length;

  useEffect(() => {
    const loadDynamicContent = async () => {
      try {
        const [carouselData, announcementData, contactQrData, friendlyLinksData] = await Promise.all([
          getPublicChannelItems('home_carousel'),
          listPublicAnnouncements({ page: 1, page_size: 5 }),
          getPublicChannelItems('home_contact_qrcodes'),
          getPublicChannelItems('friendly_links'),
        ]);

        if (carouselData?.results?.length) {
          const slides: CarouselSlide[] = carouselData.results
            .map((item) => {
              const points = Array.isArray(item.extra_json?.points)
                ? item.extra_json.points.map((x: any) => String(x))
                : [];
              return {
                title: item.title,
                desc: item.summary || '',
                points,
                image: item.image_url || '',
                link: item.link_url || '',
              };
            })
            .filter((x) => x.title && x.image);
          if (slides.length) {
            setCarouselSlides(slides);
            setSlideIndex(0);
          }
        }

        setAnnouncementRows(announcementData?.results || []);
        setAnnouncementTotal(announcementData?.count || 0);
        setContactQrRows(
          (contactQrData?.results || [])
            .map((item) => {
              const image = (item.image_url || '').trim() || pickFirstImageFromBody(item.body || '');
              return {
                id: item.id,
                title: item.title || '联系方式二维码',
                image,
                summary: item.summary || '',
              };
            })
            .filter((item) => item.image)
        );
        setFriendlyLinks(
          (friendlyLinksData?.results || [])
            .map((item) => ({
              id: item.id,
              title: String(item.title || '').trim(),
              url: String(item.link_url || '').trim(),
            }))
            .filter((item) => item.title && item.url)
        );
      } catch {
        setCarouselSlides(fallbackCarouselSlides);
        setAnnouncementRows([]);
        setAnnouncementTotal(0);
        setContactQrRows([]);
        setFriendlyLinks([]);
      }
    };
    loadDynamicContent();
  }, []);

  useEffect(() => {
    if (carouselPaused || totalSlides <= 1) return;
    const timer = window.setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % totalSlides);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [carouselPaused, totalSlides]);

  const goPrevSlide = () => {
    if (totalSlides <= 1) return;
    setSlideIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goNextSlide = () => {
    if (totalSlides <= 1) return;
    setSlideIndex((prev) => (prev + 1) % totalSlides);
  };

  const safeSlideIndex = totalSlides > 0 ? Math.min(slideIndex, totalSlides - 1) : 0;
  const currentSlide = normalizedSlides[safeSlideIndex];
  const currentLink = (currentSlide?.link || '').trim();
  const currentSlideExternal = isExternalUrl(currentLink);

  const activeBiz = useMemo(
    () => businessBlocks.find((x) => x.key === activeBizKey) || businessBlocks[0],
    [activeBizKey]
  );

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 py-8">
        <div
          className="relative mx-auto h-[420px] w-[1160px] max-w-full overflow-hidden rounded-2xl bg-white shadow"
          onMouseEnter={() => setCarouselPaused(true)}
          onMouseLeave={() => setCarouselPaused(false)}
        >
          <div className="flex h-full flex-col">
            <div className="grid min-h-0 flex-1 items-start gap-4 p-6 md:grid-cols-[1fr,1.45fr] md:p-7">
              <div className="h-full overflow-auto pr-1">
                <h1 className="text-2xl font-normal leading-tight text-gray-900">
                  {currentSlide?.title || ''}
                </h1>
                <p className="mt-4 text-base leading-8 text-gray-600">
                  {currentSlide?.desc || ''}
                </p>
                <ul className="mt-3 space-y-1 text-sm text-gray-700">
                  {(currentSlide?.points || []).map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-[#E73529]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="h-full overflow-hidden rounded-xl">
                {currentLink ? (
                  <a
                    href={currentLink}
                    target={currentSlideExternal ? '_blank' : undefined}
                    rel={currentSlideExternal ? 'noopener noreferrer' : undefined}
                    className="block h-full w-full"
                  >
                    <img
                      src={currentSlide?.image || ''}
                      alt={currentSlide?.title || 'carousel'}
                      className="h-full w-full object-contain"
                    />
                  </a>
                ) : (
                  <img
                    src={currentSlide?.image || ''}
                    alt={currentSlide?.title || 'carousel'}
                    className="h-full w-full object-contain"
                  />
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-3">
              <button
                type="button"
                onClick={goPrevSlide}
                aria-label="上一张"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#d9eaff] bg-white text-sm text-[#2f82e8] hover:bg-[#eef6ff]"
              >
                &lt;
              </button>
              <div className="flex items-center gap-2">
                {normalizedSlides.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSlideIndex(idx)}
                    className={`h-2.5 w-2.5 rounded-full transition-all ${
                      idx === slideIndex ? 'bg-[#3D92F9]' : 'bg-[#c7defa]'
                    }`}
                    aria-label={`slide-${idx + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={goNextSlide}
                aria-label="下一张"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#d9eaff] bg-white text-sm text-[#2f82e8] hover:bg-[#eef6ff]"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-8">
        <h2 className="text-center text-3xl font-normal text-gray-900">服务板块</h2>
      </section>

      <section className="mx-auto grid max-w-6xl gap-4 px-6 pb-6 md:grid-cols-4">
        {businessBlocks.map((item) => {
          const active = hoverBizKey === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onMouseEnter={() => {
                setHoverBizKey(item.key);
                setActiveBizKey(item.key);
              }}
              onMouseLeave={() => setHoverBizKey(null)}
              onFocus={() => {
                setHoverBizKey(item.key);
                setActiveBizKey(item.key);
              }}
              onClick={() => setActiveBizKey(item.key)}
              className={`rounded-xl p-4 text-left transition ${
                active
                  ? 'border border-[#d9eaff] bg-white shadow-md'
                  : 'border border-transparent bg-white'
              }`}
            >
              <div className="mb-3 h-28 overflow-hidden rounded-lg bg-gray-50">
                <img src={item.image} alt={`${item.title}配图`} className="h-full w-full object-cover" />
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium text-gray-900">{item.title}</h3>
                <span className={`text-xs font-semibold ${item.status === '已开通' ? 'text-[#2f82e8]' : 'text-[#E73529]'}`}>
                  {item.status}
                </span>
              </div>
            </button>
          );
        })}
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-14">
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">{activeBiz.title}</h3>
          <p className="mt-3 text-sm leading-8 text-gray-700">{activeBiz.overview}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-8">
        <h2 className="text-center text-3xl font-normal text-gray-900">服务优势</h2>
      </section>

      <section className="mx-auto grid max-w-6xl gap-4 px-6 pb-14 md:grid-cols-3">
        {serviceAdvantages.map((item) => (
          <div key={item} className="rounded-xl bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#3D92F9] text-xs font-bold text-white">
                ✓
              </span>
              <p className="text-base font-medium text-gray-700">{item}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="mx-auto grid max-w-6xl gap-4 px-6 pb-14 md:grid-cols-2">
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">联系方式</h3>
          <div className="mt-3 space-y-2 text-sm text-gray-600">
            <p>公司名称：安飞供应链（AnFei Supply Chain）</p>
            <p>官网：www.anfeihub.com</p>
            <p>邮箱：contact@anfeihub.com</p>
            <p>电话：+1 857-333-5300</p>
            <p>地址：Nashua NH 03060 USA</p>
          </div>
          {contactQrRows.length ? (
            <div className="mt-5">
              <div className="flex flex-wrap gap-4">
                {contactQrRows.map((qr) => (
                  <button
                    key={qr.id}
                    type="button"
                    className="flex w-[110px] flex-col items-center gap-2 rounded-lg border border-gray-100 bg-white p-2 text-center hover:border-[#bcdcff] hover:shadow-sm"
                    onClick={() => setPreviewQr(qr)}
                    title={qr.title}
                  >
                    <img
                      src={qr.image}
                      alt={qr.title}
                      className="h-24 w-24 rounded border border-gray-100 bg-white object-contain p-1"
                    />
                    <span className="line-clamp-1 text-xs text-gray-700">{qr.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">近期公告</h3>
          <div className="mt-3 space-y-3 text-sm text-gray-700">
            {announcementRows.length ? (
              <>
                {announcementRows.map((item) => {
                  const displayTime = (item.published_at || item.created_at || '').replace('T', ' ').slice(0, 19);
                  return (
                    <div key={item.id} className="flex items-center justify-between gap-3 rounded border border-gray-100 px-3 py-2">
                      <Link
                        to={`/announcements/${item.id}`}
                        className="truncate text-blue-600 hover:text-blue-700 hover:underline"
                        title={item.title}
                      >
                        {item.title}
                      </Link>
                      <span className="shrink-0 text-xs text-gray-500">{displayTime || '-'}</span>
                    </div>
                  );
                })}
                {announcementTotal > 5 ? (
                  <div className="pt-1 text-right">
                    <Link to="/announcements" className="text-sm text-blue-600 hover:text-blue-700 hover:underline">
                      查看更多...
                    </Link>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-gray-500">暂无公告内容</p>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-8">
        <h2 className="text-center text-3xl font-normal text-gray-900">友情链接</h2>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <div className="mt-1 flex flex-wrap gap-2">
            {friendlyLinks.length ? (
              friendlyLinks.map((item) => {
                const external = isExternalUrl(item.url);
                return (
                  <a
                    key={item.id}
                    href={item.url}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                    className="rounded bg-[#f4f9ff] px-3 py-1 text-sm font-medium text-[#2f82e8] hover:underline"
                    title={item.url}
                  >
                    {item.title}
                  </a>
                );
              })
            ) : (
              <span className="text-sm text-gray-500">暂无友情链接</span>
            )}
          </div>
        </div>
      </section>
      {previewQr ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4"
          onClick={() => setPreviewQr(null)}
        >
          <div
            className="w-full max-w-[560px] rounded-xl bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-base font-semibold text-gray-900">{previewQr.title}</h4>
              <button
                type="button"
                className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => setPreviewQr(null)}
              >
                关闭
              </button>
            </div>
            <img
              src={previewQr.image}
              alt={previewQr.title}
              className="mx-auto h-[460px] w-[460px] max-w-full rounded border border-gray-100 bg-white object-contain p-2"
            />
            {previewQr.summary ? <p className="mt-3 text-sm text-gray-600">{previewQr.summary}</p> : null}
          </div>
        </div>
      ) : null}
    </MarketingLayout>
  );
};

export default MarketingHomePage;

